import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useImpersonation } from "../context/ImpersonationContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import DateField from "../components/DateField";
import CourtListenerSearch from "../components/CourtListenerSearch";
import StateCourtPanel, { STATE_CONFIG } from "../components/StateCourtPanel";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import LookupTour from "../components/LookupTour";
import AddressAutocomplete from "../components/AddressAutocomplete";
import CreditorNameAutocomplete from "../components/CreditorNameAutocomplete";
import { PlayCircle, Question } from "@phosphor-icons/react";
import { toast } from "sonner";

// Best-effort parse of "Smith vs. Acme Corp" / "Smith v. Acme Corp" → creditor=second party
function parseCreditorFromCaseName(name) {
    if (!name) return "";
    const m = name.match(/\b(?:v|vs|vs\.)\.?\s+(.+)$/i);
    return (m ? m[1] : "").trim().replace(/\s+et\s+al\.?$/i, "");
}

export default function NewCase() {
    const { user } = useAuth();
    const { impersonation } = useImpersonation();
    const nav = useNavigate();
    const [clients, setClients] = useState([]);
    const [form, setForm] = useState({
        title: "",
        case_type: "lien",
        creditor_name: "",
        creditor_email: "",
        debtor_name: "",
        amount_owed: "",
        original_amount: "",
        case_number: "",
        jurisdiction: "",
        filed_date: "",
        deadline: "",
        status: "new",
        client_name: "",
        client_id: "",
        notes: "",
    });
    const emptyAddr = { line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", formatted: "", verified: false };
    const [debtorAddr, setDebtorAddr] = useState(emptyAddr);
    const [creditorAddr, setCreditorAddr] = useState(emptyAddr);
    const [saving, setSaving] = useState(false);
    const [pendingDocketId, setPendingDocketId] = useState(null);
    const [pendingStateCapture, setPendingStateCapture] = useState(null);
    const [selectedState, setSelectedState] = useState("NC");
    const [lookupTab, setLookupTab] = useState("cl");
    const [tourOpen, setTourOpen] = useState(false);
    const [params, setParams] = useSearchParams();
    const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

    // Auto-launch tour on ?tour=1 OR on the user's first visit
    useEffect(() => {
        const forceTour = params.get("tour") === "1";
        const seen = typeof window !== "undefined" && localStorage.getItem("ll_lookup_tour_seen") === "1";
        if (forceTour || !seen) {
            const t = setTimeout(() => setTourOpen(true), 350);
            // Strip the query param so a refresh doesn't keep relaunching
            if (forceTour) {
                params.delete("tour");
                setParams(params, { replace: true });
            }
            return () => clearTimeout(t);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const tourSteps = useMemo(() => ([
        {
            selector: '[data-testid="lookup-tabs"]',
            title: "Two ways to look up a judgment",
            body: "Federal RECAP/PACER for U.S. District matters, or 14 state portals for consumer judgments & liens. Most users want State court.",
        },
        {
            selector: '[data-testid="lookup-tab-cl"]',
            title: "Federal — CourtListener",
            body: "Type a party name or case number, pick the right docket, then click Import. We pre-fill the form with case data automatically.",
            beforeShow: () => setLookupTab("cl"),
        },
        {
            selector: '[data-testid="lookup-tab-state"]',
            title: "State court — 14 states live",
            body: "NC, TX, FL, VA, SC, NY, CA, OH, WV, PA, MD, CT, ME, WA. Click this tab when your judgment is at the state level (most consumer cases).",
            beforeShow: () => setLookupTab("state"),
        },
        {
            selector: '[data-testid="lookup-state-select"]',
            title: "Pick your state",
            body: "The form below adapts — counties appear where required, and we deep-link to the official portal with your search ready to paste.",
            beforeShow: () => setLookupTab("state"),
        },
        {
            selector: `[data-testid="state-open-portal-${selectedState}"]`,
            title: "Open the portal",
            body: "We open the official site in a new tab and copy your search query to your clipboard. Paste on the portal, find the record, then click Capture details to bring it back here.",
            beforeShow: () => setLookupTab("state"),
        },
        {
            selector: '[data-testid="new-case-form"]',
            title: "Fill in the rest & save",
            body: "Most fields auto-populate from the lookup. Add amount owed and (optional) client/notes, then Save. You're done in under 30 seconds.",
        },
    ]), [selectedState]);

    const applyDocket = (r) => {
        const creditor = parseCreditorFromCaseName(r.case_name);
        setForm((prev) => ({
            ...prev,
            title: prev.title || r.case_name || "",
            creditor_name: prev.creditor_name || creditor || "",
            case_number: prev.case_number || r.docket_number || "",
            jurisdiction: prev.jurisdiction || r.court_citation || "",
            filed_date: prev.filed_date || (r.date_filed || "").slice(0, 10),
        }));
        setPendingDocketId(r.docket_id);
        toast.success("Docket loaded — review the fields and save");
    };

    const applyStateCapture = (c) => {
        const stateCode = c.state || "NC";
        const countyLabel = c.county ? ` — ${c.county} County` : "";
        setForm((prev) => ({
            ...prev,
            title: prev.title || (c.plaintiff && c.defendant ? `${c.plaintiff} v. ${c.defendant}` : c.plaintiff || c.defendant || prev.title),
            creditor_name: prev.creditor_name || c.plaintiff || "",
            case_number: prev.case_number || c.case_number || "",
            jurisdiction: prev.jurisdiction || `${stateCode}${countyLabel}`,
            state: stateCode,
            filed_date: prev.filed_date || (c.filed_date || "").slice(0, 10),
            amount_owed: prev.amount_owed || (c.judgment_amount ? String(c.judgment_amount) : ""),
            case_type: prev.case_type || (
                ["tax_lien", "mechanics_lien", "ucc_lien", "code_enforcement_lien"].includes(c.judgment_type)
                    ? "lien" : "judgment"
            ),
        }));
        setPendingStateCapture(c);
    };

    useEffect(() => {
        if (user?.role === "business") {
            api.get("/clients").then((r) => {
                setClients(r.data);
                // Auto-prefill client field when in Client Focus mode
                if (impersonation?.id) {
                    const focused = r.data.find((c) => c.id === impersonation.id);
                    if (focused) {
                        setForm((f) => (
                            f.client_id ? f : { ...f, client_id: focused.id, client_name: focused.name }
                        ));
                    }
                }
            }).catch(() => {});
        }
    }, [user, impersonation]);

    const submit = async (e) => {
        e.preventDefault();
        const isValidAddr = (a) => a.line1 && a.city && a.state && a.postal_code;
        if (!isValidAddr(debtorAddr)) {
            toast.error("Please complete the debtor mailing address");
            return;
        }
        if (!isValidAddr(creditorAddr)) {
            toast.error("Please complete the creditor mailing address");
            return;
        }
        setSaving(true);
        try {
            const payload = {
                ...form,
                creditor_email: form.creditor_email || null,
                debtor_name: form.debtor_name || null,
                amount_owed: parseFloat(form.amount_owed) || 0,
                original_amount: form.original_amount ? parseFloat(form.original_amount) : null,
                client_id: form.client_id || null,
                debtor_address: debtorAddr,
                creditor_address: creditorAddr,
            };
            const { data } = await api.post("/cases", payload);
            // If user pre-filled from a CourtListener docket, auto-import the full record now
            if (pendingDocketId) {
                try {
                    await api.post("/court/import", { case_id: data.id, docket_id: pendingDocketId });
                    toast.success("Court record imported");
                } catch (e) {
                    toast.warning("Case saved, but docket import failed — try again from the Court Record tab");
                }
            }
            if (pendingStateCapture) {
                const stateCode = pendingStateCapture.state || "NC";
                const endpoint = STATE_CONFIG[stateCode]?.captureEndpoint;
                if (endpoint) {
                    try {
                        await api.post(endpoint, {
                            case_id: data.id,
                            case_number: pendingStateCapture.case_number,
                            county: pendingStateCapture.county || undefined,
                            judgment_date: pendingStateCapture.judgment_date || null,
                            judgment_amount: pendingStateCapture.judgment_amount,
                            judgment_type: pendingStateCapture.judgment_type,
                            plaintiff: pendingStateCapture.plaintiff || null,
                            defendant: pendingStateCapture.defendant || null,
                            court_type: pendingStateCapture.court_type || null,
                            filed_date: pendingStateCapture.filed_date || null,
                            portal_url: pendingStateCapture.portal_url || null,
                            notes: pendingStateCapture.notes || null,
                        });
                        toast.success(`${stateCode} judgment record attached to the case`);
                    } catch (e) {
                        toast.warning(`Case saved, but ${stateCode} capture failed — try again from the Court Record tab`);
                    }
                }
            }
            if (!pendingDocketId && !pendingStateCapture) {
                toast.success("Case created");
            }
            nav(`/cases/${data.id}`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="max-w-3xl space-y-8" data-testid="new-case-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <p className="overline">Open a case</p>
                    <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-tight">New Lien or Judgment</h1>
                    <p className="text-slate-600 mt-3 text-sm max-w-2xl">
                        Look up your matter in a public court archive to pre-fill the form automatically, or skip and fill the fields manually below.
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setTourOpen(true)}
                        data-testid="newcase-take-tour"
                    >
                        <PlayCircle size={14} weight="fill" className="mr-1.5" />
                        Take the tour
                    </Button>
                    <Link to="/help/lookup">
                        <Button type="button" variant="ghost" size="sm" data-testid="newcase-help-link">
                            <Question size={14} className="mr-1.5" />
                            How to look up records
                        </Button>
                    </Link>
                </div>
            </div>

            <Tabs value={lookupTab} onValueChange={setLookupTab} className="space-y-3" data-testid="lookup-tabs">
                <TabsList>
                    <TabsTrigger value="cl" data-testid="lookup-tab-cl">Federal (CourtListener)</TabsTrigger>
                    <TabsTrigger value="state" data-testid="lookup-tab-state">State court</TabsTrigger>
                </TabsList>
                <TabsContent value="cl">
                    <CourtListenerSearch onSelect={applyDocket} />
                </TabsContent>
                <TabsContent value="state" className="space-y-3">
                    <div className="flex items-center gap-2">
                        <label htmlFor="state-select" className="text-xs text-slate-600 font-medium uppercase tracking-wider">State</label>
                        <Select value={selectedState} onValueChange={setSelectedState}>
                            <SelectTrigger id="state-select" className="h-10 w-72" data-testid="lookup-state-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(STATE_CONFIG).map(([code, cfg]) => (
                                    <SelectItem key={code} value={code} data-testid={`lookup-state-option-${code}`}>
                                        {code} — {cfg.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <StateCourtPanel key={selectedState} stateCode={selectedState} onSelect={applyStateCapture} />
                </TabsContent>
            </Tabs>

            {pendingDocketId && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-center justify-between" data-testid="docket-pending-banner">
                    <span>Docket <span className="font-mono">{pendingDocketId}</span> queued — the full court record will be imported automatically when you save.</span>
                    <button
                        type="button"
                        onClick={() => setPendingDocketId(null)}
                        className="text-xs underline ml-3 shrink-0"
                        data-testid="docket-clear"
                    >
                        Clear
                    </button>
                </div>
            )}

            {pendingStateCapture && (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 flex items-center justify-between" data-testid="state-pending-banner">
                    <span>
                        {pendingStateCapture.state} judgment <span className="font-mono">{pendingStateCapture.case_number}</span>
                        {pendingStateCapture.county ? ` (${pendingStateCapture.county} County)` : ""} queued — will be attached to the case on save.
                    </span>
                    <button
                        type="button"
                        onClick={() => setPendingStateCapture(null)}
                        className="text-xs underline ml-3 shrink-0"
                        data-testid="state-pending-clear"
                    >
                        Clear
                    </button>
                </div>
            )}

            <form onSubmit={submit} noValidate className="space-y-6 bg-white border border-slate-900 rounded-md p-8" data-testid="new-case-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <Label htmlFor="title">Case title *</Label>
                        <Input data-testid="case-title" id="title" required value={form.title} onChange={set("title")} className="mt-1.5 h-11" placeholder="e.g. Discover Bank vs. Garcia" />
                    </div>
                    <div>
                        <Label>Type *</Label>
                        <Select value={form.case_type} onValueChange={(v) => setForm({ ...form, case_type: v })}>
                            <SelectTrigger data-testid="case-type" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="lien">Lien</SelectItem>
                                <SelectItem value="judgment">Judgment</SelectItem>
                                <SelectItem value="collections">Collections</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Status</Label>
                        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                            <SelectTrigger data-testid="case-status" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {["new","investigating","disputing","negotiating","settled","closed"].map((s) => (
                                    <SelectItem key={s} value={s}>{s[0].toUpperCase()+s.slice(1)}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label htmlFor="creditor">Creditor / Plaintiff *</Label>
                        <CreditorNameAutocomplete
                            value={form.creditor_name}
                            onChange={(v) => setForm((prev) => ({ ...prev, creditor_name: v }))}
                            onEmailSuggest={(email) => {
                                if (!form.creditor_email && email) {
                                    setForm((prev) => ({ ...prev, creditor_email: email }));
                                    toast.success(`Auto-filled dispute email: ${email}`);
                                }
                            }}
                            required
                        />
                    </div>
                    <div>
                        <Label htmlFor="creditor_email">Creditor email <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
                        <Input
                            data-testid="case-creditor-email"
                            id="creditor_email"
                            type="email"
                            value={form.creditor_email}
                            onChange={set("creditor_email")}
                            placeholder="collections@creditor.com"
                            className="mt-1.5 h-11"
                        />
                        <p className="text-xs text-slate-500 mt-1">Enables one-click emailing of signed letters from the Documents page.</p>
                    </div>
                    <div className="md:col-span-2">
                        <Label htmlFor="debtor_name">Debtor / sender name <span className="text-slate-400 text-xs font-normal">(who signs the letter)</span></Label>
                        <Input
                            data-testid="case-debtor-name"
                            id="debtor_name"
                            value={form.debtor_name}
                            onChange={set("debtor_name")}
                            placeholder="e.g. Jane Doe (leave blank to use your account name)"
                            className="mt-1.5 h-11"
                        />
                        <p className="text-xs text-slate-500 mt-1">Appears on the return address label and in the letter&apos;s signature block.</p>
                    </div>

                    {user?.role === "business" && (
                        <>
                            <div>
                                <Label>Client</Label>
                                <Select value={form.client_id || "__none__"} onValueChange={(v) => setForm({ ...form, client_id: v === "__none__" ? "" : v })}>
                                    <SelectTrigger data-testid="case-client-select" className="mt-1.5 h-11"><SelectValue placeholder="— None —" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">— None —</SelectItem>
                                        {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                {impersonation?.id && form.client_id === impersonation.id && (
                                    <p data-testid="new-case-focus-hint" className="text-xs text-amber-700 mt-1.5">
                                        Prefilled from Client Focus mode ({impersonation.name}).
                                    </p>
                                )}
                            </div>
                            <div>
                                <Label htmlFor="client">Client name (manual)</Label>
                                <Input data-testid="case-client" id="client" value={form.client_name} onChange={set("client_name")} className="mt-1.5 h-11" placeholder="Optional if not linked to a client above" />
                            </div>
                        </>
                    )}
                    <div>
                        <Label htmlFor="amt">Amount owed (USD) *</Label>
                        <Input data-testid="case-amount" id="amt" type="number" min="0" step="0.01" required value={form.amount_owed} onChange={set("amount_owed")} className="mt-1.5 h-11" />
                    </div>
                    <div>
                        <Label htmlFor="orig">Original amount</Label>
                        <Input data-testid="case-original" id="orig" type="number" min="0" step="0.01" value={form.original_amount} onChange={set("original_amount")} className="mt-1.5 h-11" />
                    </div>
                    <div>
                        <Label htmlFor="num">Case / account #</Label>
                        <Input data-testid="case-number" id="num" value={form.case_number} onChange={set("case_number")} className="mt-1.5 h-11" />
                    </div>
                    <div>
                        <Label htmlFor="jur">Jurisdiction</Label>
                        <Input data-testid="case-jurisdiction" id="jur" value={form.jurisdiction} onChange={set("jurisdiction")} className="mt-1.5 h-11" placeholder="e.g. CA — Los Angeles County" />
                    </div>
                    <div>
                        <Label htmlFor="filed">Filed date</Label>
                        <DateField data-testid="case-filed" id="filed" value={form.filed_date} onChange={set("filed_date")} />
                    </div>
                    <div>
                        <Label htmlFor="deadline">Response deadline</Label>
                        <DateField data-testid="case-deadline" id="deadline" value={form.deadline} onChange={set("deadline")} />
                    </div>
                    <div className="md:col-span-2">
                        <Label htmlFor="notes">Notes / facts</Label>
                        <Textarea data-testid="case-notes" id="notes" rows={4} value={form.notes} onChange={set("notes")} className="mt-1.5" placeholder="Background, defenses, communications…" />
                    </div>
                </div>

                <div className="pt-4 border-t border-slate-200">
                    <p className="overline">Mailing Addresses</p>
                    <h3 className="font-serif text-xl text-[#0F1E3D] mt-1">Required for AI-generated letters</h3>
                    <p className="text-sm text-slate-500 mt-1">
                        Both addresses appear at the top of every dispute or settlement letter and are referenced in the body so recipients know exactly where to send responses.
                    </p>
                    <div className="grid md:grid-cols-2 gap-6 mt-4">
                        <AddressAutocomplete
                            label="Debtor / Client mailing address"
                            value={debtorAddr}
                            onChange={setDebtorAddr}
                            required
                            testIdPrefix="debtor-addr"
                            hint="Where the creditor should send written responses."
                            onValidate={(payload) => api.post("/address/validate", payload).then((r) => r.data).catch(() => null)}
                        />
                        <AddressAutocomplete
                            label="Creditor mailing address"
                            value={creditorAddr}
                            onChange={setCreditorAddr}
                            required
                            testIdPrefix="creditor-addr"
                            hint="Where the letter will be mailed."
                            onValidate={(payload) => api.post("/address/validate", payload).then((r) => r.data).catch(() => null)}
                        />
                    </div>
                </div>

                <div className="flex gap-3">
                    <Button data-testid="case-save-btn" type="submit" disabled={saving} className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-11 px-6">
                        {saving ? "Saving…" : "Create case"}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => nav("/cases")} className="h-11">Cancel</Button>
                </div>
            </form>

            <LookupTour
                steps={tourSteps}
                open={tourOpen}
                onClose={() => {
                    setTourOpen(false);
                    localStorage.setItem("ll_lookup_tour_seen", "1");
                }}
                onComplete={() => localStorage.setItem("ll_lookup_tour_seen", "1")}
            />
        </div>
    );
}
